import UserProfileButton from '@efdigital/ll-xceed-v2/components/user/user-profile-button.gts';
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';

export type NavItem = {
  link: {
    route: string;
    models?: unknown[];
    disabled?: boolean;
  };
  icon: string;
  label: string;
  css?: string;
  /** Count shown beside the label (e.g. new messages, sessions to action). */
  badge?: number;
  /**
   * What the badge counts, for screen readers — "3 unread", "3 need
   * attention". Defaults to `unread`, which is what most of them are.
   */
  badgeLabel?: string;
};

interface SidebarContainerSignature {
  Args: {
    nav?: NavItem[];
    helpRoute?: string;
    /** When set, the user profile button links to this route (e.g. settings). */
    profileRoute?: string;
    /** Profile photo for the button; falls back to initials when absent. */
    profileAvatarUrl?: string | null;
  };
  Element: HTMLElement;
  Blocks: {
    sidebar: [className: string];
    content: [];
  };
}

const EMPTY_ARRAY: unknown[] = [];

const SidebarContainer: TemplateOnlyComponent<SidebarContainerSignature> =
  <template>
    <div class="row g-0 flex-grow-1 min-h-0">
      <div class="col-xl-3 d-none d-xl-block border-end">
        <aside
          class="d-flex flex-column h-100 overflow-hidden ll-bg-chrome ll-sidebar-nav sticky-top"
          aria-label="Navigation"
          ...attributes
        >
          {{#if (has-block "sidebar")}}
            <div
              class="{{if @nav 'flex-shrink-0 border-bottom' 'flex-grow-1'}}
                border-primary-subtle ll-bg-chrome"
            >
              {{yield "p-3 overflow-auto min-h-0" to="sidebar"}}
            </div>
          {{/if}}

          {{#if @nav}}
            <nav
              class="nav nav-pills flex-column gap-1 p-3 flex-grow-1 overflow-auto min-h-0"
              aria-label="Main navigation"
            >
              {{#each @nav as |item|}}
                <LinkTo
                  @route={{item.link.route}}
                  @models={{if item.link.models item.link.models EMPTY_ARRAY}}
                  @disabled={{item.link.disabled}}
                  class="nav-link d-flex align-items-center gap-3 {{item.css}}"
                >
                  <iconify-icon inline icon={{item.icon}} aria-hidden="true" />
                  <span class="flex-grow-1">{{item.label}}</span>
                  {{#if item.badge}}
                    <span
                      class="badge rounded-pill text-bg-primary coaching-sidebar-nav-badge fw-semibold d-inline-flex align-items-center justify-content-center"
                      aria-label="{{item.badge}}
                        {{if item.badgeLabel item.badgeLabel 'unread'}}"
                    >{{item.badge}}</span>
                  {{/if}}
                </LinkTo>
              {{/each}}
            </nav>
          {{/if}}

          <div class="flex-shrink-0 ll-bg-chrome">
            {{#if @helpRoute}}
              <div class="px-3 pt-2 pb-4">
                <LinkTo
                  @route={{@helpRoute}}
                  class="btn btn-outline-primary d-flex align-items-center gap-2 w-100 fw-semibold"
                >
                  <iconify-icon
                    inline
                    icon="ph:question-fill"
                    class="fs-5"
                    aria-hidden="true"
                  />
                  Help
                </LinkTo>
              </div>
            {{/if}}

            <UserProfileButton
              @route={{@profileRoute}}
              @avatarUrl={{@profileAvatarUrl}}
            />
          </div>
        </aside>
      </div>

      {{! No overflow-hidden here: the app scrolls the window, so this column
          grows to content height and clips nothing — but an overflow:hidden
          ancestor silently breaks position:sticky for descendants (e.g. the
          coach profile editor's section nav). min-w-0 still guards against
          flex children forcing horizontal overflow. }}
      <main
        id="main-content"
        tabindex="-1"
        class="col-12 col-xl-9 d-flex flex-column flex-grow-1 min-w-0 min-h-0 bg-body"
      >
        {{yield to="content"}}
      </main>
    </div>

    <div
      class="offcanvas offcanvas-start d-xl-none"
      tabindex="-1"
      id="sidebarMobileMenu"
      aria-labelledby="sidebarMobileMenuLabel"
    >
      <div class="offcanvas-header border-bottom">
        <h2 class="offcanvas-title h5 mb-0" id="sidebarMobileMenuLabel">
          Menu
        </h2>
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="offcanvas"
          aria-label="Close menu"
        ></button>
      </div>
      <div class="offcanvas-body p-0">
        <nav
          class="nav nav-pills flex-column gap-1"
          aria-label="Mobile navigation"
        >
          {{#if (has-block "sidebar")}}
            {{yield "p-3 overflow-auto min-h-0" to="sidebar"}}
          {{/if}}

          {{#if @nav}}
            <div class="p-3">
              {{#each @nav as |item|}}
                <LinkTo
                  @route={{item.link.route}}
                  @models={{if item.link.models item.link.models EMPTY_ARRAY}}
                  @disabled={{item.link.disabled}}
                  class="nav-link d-flex align-items-center gap-3"
                  data-bs-dismiss="offcanvas"
                >
                  <iconify-icon
                    inline
                    icon={{item.icon}}
                    class="fs-5"
                    aria-hidden="true"
                  />
                  <span class="flex-grow-1">{{item.label}}</span>
                  {{#if item.badge}}
                    <span
                      class="badge rounded-pill text-bg-primary coaching-sidebar-nav-badge fw-semibold d-inline-flex align-items-center justify-content-center"
                      aria-label="{{item.badge}}
                        {{if item.badgeLabel item.badgeLabel 'unread'}}"
                    >{{item.badge}}</span>
                  {{/if}}
                </LinkTo>
              {{/each}}
            </div>
          {{/if}}

          {{#if @helpRoute}}
            <div class="px-3 pt-2 pb-4">
              <LinkTo
                @route={{@helpRoute}}
                class="nav-link d-flex align-items-center gap-3"
                data-bs-dismiss="offcanvas"
              >
                <iconify-icon
                  inline
                  icon="ph:question"
                  class="fs-5"
                  aria-hidden="true"
                />
                Help
              </LinkTo>
            </div>
          {{/if}}
          <UserProfileButton
            @route={{@profileRoute}}
            @avatarUrl={{@profileAvatarUrl}}
          />

        </nav>
      </div>
    </div>
  </template>;

export default SidebarContainer;
